import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AddIcon from "@mui/icons-material/Add";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getTokenClaims } from "../../core/auth/auth";
import { type UserRead, usersApi } from "../../core/api/usersApi";

const PROVIDER_LABEL: Record<string, string> = {
  local: "Local",
  google: "Google",
  microsoft: "Microsoft",
};

const ALL_ROLES = ["admin", "user_manager", "shift", "tax", "user"] as const;
type RoleName = (typeof ALL_ROLES)[number];

const ROLE_LABEL: Record<RoleName, string> = {
  admin: "Admin",
  user_manager: "Gestor de usuarios",
  shift: "Turnos",
  tax: "Fiscal",
  user: "Usuario",
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

const Section = ({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) => (
  <Box
    sx={{
      bgcolor: "var(--color-surface)",
      border: "1px solid var(--color-border)",
      borderRadius: 2,
      p: 3,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
      <Box sx={{ color: "var(--color-text-secondary)", display: "flex" }}>{icon}</Box>
      <Typography fontWeight={600} fontSize={15}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

// ---------------------------------------------------------------------------

const UserProfile = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<UserRead | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Name edit
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [nameError, setNameError] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSuccess, setNameSuccess] = useState(false);

  // Active toggle
  const [activeLoading, setActiveLoading] = useState(false);

  // Roles
  const [roleLoading, setRoleLoading] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const canManage = ["admin", "user_manager"].some((r) =>
    getTokenClaims()?.roles.includes(r),
  );


  useEffect(() => {
    if (!userId) return;
    usersApi
      .get(userId)
      .then((u) => {
        setUser(u);
        setNameValue(u.name);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [userId]);

  const reload = async () => {
    if (!userId) return;
    const u = await usersApi.get(userId);
    setUser(u);
  };

  // ── Name ────────────────────────────────────────────────────

  const handleSaveName = async () => {
    if (nameValue.trim().length < 2) { setNameError("Mínimo 2 caracteres."); return; }
    setNameSaving(true);
    setNameError("");
    try {
      const updated = await usersApi.update(user!.id, { name: nameValue.trim() });
      setUser(updated);
      setNameValue(updated.name);
      setEditingName(false);
      setNameSuccess(true);
      setTimeout(() => setNameSuccess(false), 3000);
    } catch {
      setNameError("No se pudo guardar el nombre.");
    } finally {
      setNameSaving(false);
    }
  };

  const handleCancelName = () => {
    setEditingName(false);
    setNameValue(user?.name ?? "");
    setNameError("");
  };

  // ── Active ──────────────────────────────────────────────────

  const handleToggleActive = async () => {
    if (!user) return;
    setActiveLoading(true);
    try {
      const updated = await usersApi.update(user.id, { active: !user.active });
      setUser(updated);
    } finally {
      setActiveLoading(false);
    }
  };

  // ── Roles ───────────────────────────────────────────────────

  const currentRoleNames = (user?.roles ?? []) as RoleName[];
  const availableToAdd = ALL_ROLES.filter((r) => !currentRoleNames.includes(r));

  const handleAddRole = async (role: RoleName) => {
    if (!user) return;
    setMenuAnchor(null);
    setRoleLoading(role);
    try {
      await usersApi.addRole(user.id, role);
      await reload();
    } finally {
      setRoleLoading(null);
    }
  };

  const handleRemoveRole = async (role: string) => {
    if (!user) return;
    setRoleLoading(role);
    try {
      await usersApi.removeRole(user.id, role);
      await reload();
    } finally {
      setRoleLoading(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (notFound || !user) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/users")} sx={{ mb: 2, textTransform: "none" }}>
          Volver
        </Button>
        <Typography color="text.secondary">Usuario no encontrado.</Typography>
      </Box>
    );
  }

  const initial = user.name.charAt(0).toUpperCase();

  return (
    <Box sx={{ maxWidth: 680 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/users")}
        sx={{ mb: 2, textTransform: "none", color: "var(--color-text-secondary)" }}
      >
        Volver a usuarios
      </Button>

      <Typography variant="h5" fontWeight={600} mb={3}>
        Perfil de usuario
      </Typography>

      {/* ── Identity card ───────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          borderRadius: 2,
          p: 3,
          mb: 2,
          display: "flex",
          alignItems: "flex-start",
          gap: 2.5,
        }}
      >
        <Avatar
          sx={{
            width: 64,
            height: 64,
            fontSize: 26,
            fontWeight: 700,
            bgcolor: user.active ? "var(--color-accent)" : "var(--color-text-disabled)",
            flexShrink: 0,
          }}
        >
          {initial}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Name */}
          {editingName ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <TextField
                size="small"
                value={nameValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNameValue(e.target.value)}
                error={!!nameError}
                helperText={nameError}
                autoFocus
                sx={{ flex: 1 }}
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === "Enter") handleSaveName(); if (e.key === "Escape") handleCancelName(); }}
              />
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={nameSaving ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon fontSize="small" />}
                onClick={handleSaveName}
                disabled={nameSaving}
              >
                Guardar
              </Button>
              <Button size="small" onClick={handleCancelName} disabled={nameSaving}>
                Cancelar
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography fontWeight={600} fontSize={18} noWrap>
                {user.name}
              </Typography>
              <Tooltip title="Editar nombre">
                <IconButton size="small" onClick={() => setEditingName(true)} sx={{ color: "var(--color-text-secondary)" }}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {nameSuccess && (
                <Typography variant="caption" color="success.main">Guardado</Typography>
              )}
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" noWrap>
            {user.email}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
            <Chip
              label={PROVIDER_LABEL[user.provider] ?? user.provider}
              size="small"
              sx={{ bgcolor: "rgba(15,118,110,0.10)", color: "var(--color-accent)", fontWeight: 600, border: "none" }}
            />
            <Chip
              label={user.active ? "Activo" : "Inactivo"}
              size="small"
              sx={{
                bgcolor: user.active ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.12)",
                color: user.active ? "var(--color-success)" : "var(--color-danger)",
                fontWeight: 600,
                border: "none",
              }}
            />
          </Box>
        </Box>

        {/* Active toggle */}
        <Button
          size="small"
          variant="outlined"
          disabled={activeLoading}
          onClick={handleToggleActive}
          sx={{
            textTransform: "none",
            flexShrink: 0,
            borderColor: user.active ? "var(--color-danger)" : "var(--color-success)",
            color: user.active ? "var(--color-danger)" : "var(--color-success)",
            "&:hover": {
              borderColor: user.active ? "var(--color-danger)" : "var(--color-success)",
              bgcolor: user.active ? "rgba(220,38,38,0.06)" : "rgba(22,163,74,0.06)",
            },
          }}
        >
          {activeLoading ? <CircularProgress size={14} /> : user.active ? "Dar de baja" : "Reactivar"}
        </Button>
      </Box>

      {/* ── Roles ───────────────────────────────────────────── */}
      <Box mb={2}>
        <Section
          title="Roles"
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          }
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
            {currentRoleNames.length === 0 && !canManage && (
              <Typography variant="body2" color="text.secondary">Sin roles asignados.</Typography>
            )}

            {currentRoleNames.map((r) => (
              <Chip
                key={r}
                label={ROLE_LABEL[r] ?? r}
                size="small"
                variant="outlined"
                disabled={roleLoading === r}
                onDelete={canManage ? () => handleRemoveRole(r) : undefined}
              />
            ))}

            {canManage && availableToAdd.length > 0 && (
              <>
                <Tooltip title="Añadir rol">
                  <IconButton
                    size="small"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => setMenuAnchor(e.currentTarget)}
                    disabled={!!roleLoading}
                    sx={{ border: "1px dashed var(--color-border)", borderRadius: 1 }}
                  >
                    {roleLoading ? <CircularProgress size={14} /> : <AddIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                >
                  {availableToAdd.map((r) => (
                    <MenuItem key={r} onClick={() => handleAddRole(r)} sx={{ fontSize: 14 }}>
                      {ROLE_LABEL[r]}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Box>
        </Section>
      </Box>

      {/* ── Activity ────────────────────────────────────────── */}
      <Box mb={2}>
        <Section
          title="Actividad"
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          }
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
                Miembro desde
              </Typography>
              <Typography variant="body2" fontWeight={500}>{formatDate(user.created_at)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
                Último acceso
              </Typography>
              <Typography variant="body2" fontWeight={500}>{formatDate(user.last_login)}</Typography>
            </Box>
          </Box>
        </Section>
      </Box>

    </Box>
  );
};

export default UserProfile;
